<template>

  <div class="vflex bg-light">

    <!-- header: same height/border of the thread header it replaces -->
    <div class="d-flex align-items-center border-bottom bg-white px-3 py-2">
      <!-- there is only somewhere to go back to when the list is not empty -->
      <button
        v-if="canGoBack"
        type="button"
        class="btn btn-outline-secondary btn-sm mr-2"
        :title="$t('To go back')"
        @click="$emit('back')">
        <i class="fa-solid fa-arrow-left"></i>
      </button>
      <div class="font-weight-bold flex-grow-1">{{ $t('New contact') }}</div>
    </div>

    <div class="vflex-row-1 p-4">
      <form
        class="new-conversation-form mx-auto bg-white border rounded p-4"
        :class="{ 'box-loading': saving }"
        @submit.prevent="save">

        <div class="form-group">
          <label>{{ $t('Name') }}</label>
          <input
            v-autofocus
            v-model="form.name"
            type="text"
            class="form-control"
            :disabled="saving">
          <ValidateMessage :error="validateErrors['name']" />
        </div>

        <!-- addresses already known (the candidate phones, for instance): the
        field below stays available for any other one -->
        <div
          v-if="contacts.length"
          class="form-group">
          <label class="d-block text-uppercase text-muted small font-weight-bold mb-1">{{ $t('Contacts') }}</label>
          <div class="list-group">
            <a
              v-for="(contact, index) in contacts"
              :key="index"
              href="javascript:void(0)"
              class="list-group-item list-group-item-action d-flex align-items-center py-2"
              :class="{ active: isSelected(contact) }"
              @click="choose(contact)">
              <i
                class="contact-option-icon mr-2"
                :class="icon(contact.type)"></i>
              <span class="text-truncate">{{ contact.contact_address }}</span>
            </a>
          </div>
        </div>

        <div class="form-group">
          <ContactAddressField
            :type="form.type"
            :value="form.contact_address"
            :editing="true"
            @update:type="form.type = $event"
            @update:value="form.contact_address = $event" />
          <ValidateMessage :error="validateErrors['type']" />
          <ValidateMessage :error="validateErrors['contact_address']" />
        </div>

        <div
          v-if="errorMessage"
          class="alert alert-warning">
          {{ errorMessage }}
        </div>

        <div class="text-right">
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="saving || !canSave">
            <i
              v-if="saving"
              class="fa-solid fa-circle-notch fa-spin mr-1"></i>
            {{ $t('Create') }}
          </button>
        </div>

      </form>
    </div>

  </div>

</template>

<script>
import ContactAddressField, { contactAddressTypes } from './contact/ContactAddressField'

export default {
  name: 'NewConversation',
  components: {
    ContactAddressField
  },
  props: {
    // known addresses offered as shortcuts: [{ type, contact_address }]
    contacts: {
      type: Array,
      default: () => []
    },
    canGoBack: {
      type: Boolean,
      default: false
    },
    // name known beforehand (the candidate name, for instance): it only
    // prefills the field, which stays editable
    contactName: {
      type: String,
      default: ''
    },
    // when the form is opened from a candidate, the conversation is already
    // born associated to him
    candidateId: {
      type: String,
      default: null
    },
    // store list the new conversation is added to
    contextKey: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      saving: false,
      errorMessage: null,
      validateErrors: {},
      form: {
        name: this.contactName || '',
        type: 'whatsapp',
        contact_address: ''
      }
    }
  },
  computed: {
    // the address is what makes the contact reachable, so it is required
    canSave () {
      return !!(this.form.type && this.form.contact_address && this.form.contact_address.trim())
    }
  },
  methods: {
    // generic card icon when the type is not one of the known channels
    icon (type) {
      const definition = contactAddressTypes.find((item) => { return item.value === type })
      return definition ? definition.icon : 'fa-regular fa-address-card'
    },
    isSelected (contact) {
      return contact.type === this.form.type && contact.contact_address === this.form.contact_address
    },
    /*
    choose
    copies a known address into the form, where it can still be edited
    */
    choose (contact) {
      this.form.type = contact.type || 'whatsapp'
      this.form.contact_address = contact.contact_address || ''
    },
    /*
    save
    */
    save () {
      if (!this.canSave) {
        return
      }
      this.saving = true
      this.errorMessage = null
      this.validateErrors = {}

      const data = {
        name: this.form.name.trim(),
        type: this.form.type,
        contact_address: this.form.contact_address.trim()
      }

      if (this.candidateId) {
        data.candidate_id = this.candidateId
      }

      this.$store
      .dispatch('createConversation', { contextKey: this.contextKey, data })
      .then((conversation) => {
        this.$store.dispatch('setWarn', { status: 'success' })
        this.$emit('created', conversation)
      })
      .catch((error) => {
        const payload = error.response && error.response.data
        if (payload && payload.name === 'ValidationError') {
          this.validateErrors = payload.errors || {}
        } else {
          this.errorMessage = (payload && payload.message) || this.$t('Something went wrong')
        }
      })
      .finally(() => {
        this.saving = false
      })
    }
  }
}
</script>

<style scoped>
/* the form keeps a readable width on the wide thread column */
.new-conversation-form {
  max-width: 420px;
}

.contact-option-icon {
  width: 16px;
}
</style>



// WEBPACK FOOTER //
// src/components/Messaging/NewConversation.vue